import { BaseSyntheticEvent, useCallback, useEffect, useState } from 'react';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

export interface UseSearchParamProps {
  onSetSearchParams?:
    | ((searchParams: URLSearchParams) => void)
    | ParamKeyValuePair[]
    | Record<string, string>;
  debounce?: number;
  array?: boolean;
}

const useSearchParam = <T extends string | string[]>(
  name: string,
  { onSetSearchParams, debounce = 180, array }: UseSearchParamProps = {}
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setSearchParam = useCallback(
    (name, value: string | string[]) => {
      if (Array.isArray(value) && value.length === 0) {
        searchParams.set(name, '');
      } else if (Array.isArray(value)) {
        searchParams.delete(name);
        value.forEach((val) => searchParams.append(name, val));
      } else {
        searchParams.set(name, value);
      }
    },
    [searchParams]
  );

  const [value, setValue] = useState<T>(
    (array ? searchParams.getAll(name).filter((value) => value) : searchParams.get(name) || '') as T
  );

  useEffect(() => {
    setValue(
      (array
        ? searchParams.getAll(name).filter((value) => value)
        : searchParams.get(name) || '') as T
    );
  }, [array, name, searchParams]);

  const navigate = useDebouncedCallback(() => {
    setSearchParam(name, value);

    if (Array.isArray(onSetSearchParams)) {
      onSetSearchParams.forEach(([key, value]) => setSearchParam(key, value));
    } else if (typeof onSetSearchParams === 'object') {
      Object.keys(onSetSearchParams).forEach((key) => setSearchParam(key, onSetSearchParams[key]));
    } else {
      onSetSearchParams && onSetSearchParams(searchParams);
    }

    setSearchParams(searchParams);
  }, debounce);

  const onChange = useCallback(
    (e: string | string[] | BaseSyntheticEvent) => {
      let value = (array ? [] : '') as T;

      if (typeof e === 'string' || typeof e === 'number') {
        value = e as T;
      } else if (Array.isArray(e)) {
        value = e as T;
      } else if (
        e &&
        typeof e === 'object' &&
        'target' in e &&
        e.target &&
        e.target.type === 'checkbox'
      ) {
        value = e.target.checked ? e.target.value : '';
      } else if (e && typeof e === 'object' && 'target' in e && e.target) {
        value = e.target.value;
      }

      setValue(value);
    },
    [array]
  );

  const setParam = useCallback(
    (e?) => {
      if (e !== undefined) {
        onChange(e);
      }

      navigate();
    },
    [navigate, onChange]
  );

  return [value, setParam, onChange] as [T, typeof setParam, typeof onChange];
};

export default useSearchParam;
